import React, { useState } from "react"
import { Box, Tabs, Tab } from "@mui/material"
import { useTranslate } from "ra-core"
import BarChartIcon from "@mui/icons-material/BarChart"
import TableRowsIcon from "@mui/icons-material/TableRows"
import {
    DatagridConfigurable,
    List,
    TextField,
    DateField,
} from "react-admin"
import DDTChart from "components/charts/chart.ddt"
import { ChartProps } from "components/charts/props"

interface DDTDataTableProps {
    id: string
}

const DDTDataTable: React.FC<DDTDataTableProps> = ({ id }) => {
    const translate = useTranslate()

    return (
        <List
            resource={`devices/${id}/data/current+session`}
            sort={{ field: "value_date", order: "DESC" }}
            title={false}
            actions={false}
            perPage={15}
        >
            <DatagridConfigurable
                sx={{
                    "& .RaDatagrid-headerCell": { 
                        fontWeight: 'bold' 
                    }
                }} 
                bulkActionButtons={false} 
            >
                <TextField source="val_p" label={translate("resources.params.fields.p")} />
                <TextField source="val_t" label={translate("resources.params.fields.t")} />
                <TextField source="powerstate" label={translate("resources.params.fields.powerstate")} />
                <TextField source="rssi" label={translate("resources.params.fields.rssi")} />
                <DateField showTime source="value_date" label={translate("resources.params.fields.value_date")} />
            </DatagridConfigurable>
        </List>
    )
}

interface TabPanelProps {
    children?: React.ReactNode
    index: number
    value: number
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            style={{ height: '100%' }}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 0.5, height: '100%', boxSizing: 'border-box' }}>
                    {children}
                </Box>
            )}
        </div>
    )
}

function a11yProps(index: number) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    }
}

interface DDTViewerProps extends ChartProps {
    showRangeslider?: boolean
}

const DDTViewer: React.FC<DDTViewerProps> = (props) => {
    const [value, setValue] = useState(0)
    const translate = useTranslate()

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue)
    }

    // Стиль контейнера для графика в модальном окне
    const modalChartContainerStyle: React.CSSProperties = {
        backgroundColor: 'transparent',
        padding: '10px',
        borderRadius: '0px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
    }

    return (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
                <Tabs value={value} onChange={handleChange} aria-label="DDT data viewer tabs">
                    <Tab 
                        icon={<BarChartIcon />} 
                        label={translate("resources.devices.chart")} 
                        {...a11yProps(0)} 
                    />
                    <Tab 
                        icon={<TableRowsIcon />} 
                        label={translate("resources.devices.data")} 
                        {...a11yProps(1)} 
                    />
                </Tabs>
            </Box>
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
                <TabPanel value={value} index={0}>
                    <Box sx={{ height: '100%', overflow: 'hidden' }}>
                        <DDTChart 
                            {...props}
                            containerStyle={modalChartContainerStyle}
                        />
                    </Box>
                </TabPanel>
                <TabPanel value={value} index={1}>
                    <Box sx={{ height: '100%', overflow: 'auto' }}>
                        <DDTDataTable id={props.id} />
                    </Box>
                </TabPanel>
            </Box>
        </Box>
    )
}

export default DDTViewer